import { useEffect, useMemo, useState } from "react";
import * as ReactSelect from "react-select";
import Select from "react-select";
import { MdTrain } from "react-icons/md";
import { TransitStation, getTransitStations } from "../../Commute/transitApi";
import { buildSelectStyles } from "./selectStyles";

const { components } = ReactSelect;

type Props = {
  name: string;
  placeholder: string;
  value: TransitStation | null;
  onChange: (value: TransitStation | null) => void;
  initialSearch?: string;
  /**
   * Limit options to one line. The published Lagos lines share no station, so
   * once one end of the journey is chosen the other must stay on that line —
   * offering the rest only leads to a "no through service" dead end.
   */
  restrictToLine?: string | null;
  dark?: boolean;
  pos?: "left" | "right";
};

const normalize = (value: string) =>
  value
    .toLowerCase()
    .replace(/\b(station|terminal)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Station picker for the Lagos train. Uses the same react-select shell and
 * styling as the interstate city picker and the flight airport picker, so every
 * search tab behaves identically — only the data source differs.
 */
const StationSearchInput = ({
  name,
  placeholder,
  value,
  onChange,
  initialSearch,
  restrictToLine,
  dark = false,
  pos = "left",
}: Props) => {
  const [stations, setStations] = useState<TransitStation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    getTransitStations()
      .then(setStations)
      .catch(() => setStations([]))
      .finally(() => setIsLoading(false));
  }, []);

  const options = useMemo(
    () =>
      restrictToLine
        ? stations.filter((station) => station.lineCode === restrictToLine)
        : stations,
    [stations, restrictToLine],
  );

  // A station name handed in via the URL (?from=Marina) is resolved once the
  // list lands, so a shared link opens with the picker already filled.
  const selected = useMemo(() => {
    if (value?.code) return value;

    const initial = normalize(initialSearch || "");
    if (!initial) return null;

    return stations.find((item) => normalize(item.name) === initial) || null;
  }, [value, initialSearch, stations]);

  const CustomOption = (props: any) => (
    <components.Option {...props}>
      <div className="font-semibold capitalize cursor-pointer hover:bg-slate-100">
        <div className="flex items-center space-x-4">
          <div className="text-lg text-[#302D63]">
            <MdTrain />
          </div>
          <div className="text-sm">
            <div className="p-0 m-0">{props.data.name}</div>
            {props.data.line && (
              <p className="text-[10px] font-light uppercase p-0 m-0 tracking-wide">
                {props.data.line}
              </p>
            )}
          </div>
        </div>
      </div>
    </components.Option>
  );

  const CustomNoOptionsMessage = (props: any) => (
    <components.NoOptionsMessage {...props}>
      <span style={{ fontStyle: "italic", color: "#999" }}>
        {isLoading ? "Loading stations…" : "No station matches that search."}
      </span>
    </components.NoOptionsMessage>
  );

  return (
    <div
      className={
        dark
          ? "border-white/25 bg-white/10 outline-none flex flex-col border-[1px] py-1 h-14 rounded-[14px] w-full text-white px-3 focus-within:border-orange-400 transition-colors"
          : "border-[#302D6340] bg-white outline-none flex flex-col border-[1px] py-1 h-14 rounded-[4px] w-full text-[#171717] px-3 focus:border-b-[#EE723A] focus:border-2"
      }
    >
      <label className={dark ? "text-xs !text-white" : "text-xs !text-white"}>
        {name}
      </label>
      <Select
        options={options}
        isLoading={isLoading}
        components={{
          Option: CustomOption,
          DropdownIndicator: () => null,
          NoOptionsMessage: CustomNoOptionsMessage,
        }}
        placeholder={placeholder}
        // Default filtering only reads the label, so typing a line name
        // ("Blue") would match nothing despite it being on screen.
        filterOption={(option, raw) => {
          const q = raw.trim().toLowerCase();
          if (!q) return true;
          const station = option.data as TransitStation;
          return (
            station.name.toLowerCase().includes(q) ||
            (station.line || "").toLowerCase().includes(q)
          );
        }}
        styles={{
          ...buildSelectStyles({ dark, pos }),
          // The glass card uses backdrop-filter, which opens a new stacking
          // context — no z-index on the menu itself can then beat content
          // outside that card (the results list, the footer). Same fix
          // BasicAirlineSearchInput uses: portal the menu to <body> so it
          // stacks against the whole page, then give it real headroom above
          // the transparent nav (z-50).
          menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
        }}
        value={selected}
        onChange={(option) => onChange((option as TransitStation) || null)}
        menuPlacement="auto"
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
        menuPosition="fixed"
        getOptionLabel={(option: any) => option.name}
        getOptionValue={(option: any) => `${option.lineCode}-${option.code}`}
        isClearable
      />
    </div>
  );
};

export default StationSearchInput;
